import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, Mail } from 'lucide-react';
import { OtpVerificationInput } from './OtpVerificationInput';
import { useSendMfaOtp, useVerifyMfaOtp, useEnrollOtpMfa } from '@/hooks/useOtpMFA';
import { supabase } from '@/integrations/supabase/client';

interface EmailMfaEnrollmentProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

type Step = 'email' | 'verify';

export function EmailMfaEnrollment({ onSuccess, onCancel }: EmailMfaEnrollmentProps) {
  const { t } = useTranslation('common');
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [isLoadingEmail, setIsLoadingEmail] = useState(true);

  const sendOtp = useSendMfaOtp();
  const _verifyOtp = useVerifyMfaOtp();
  const enrollMfa = useEnrollOtpMfa();

  // Load user's email
  useEffect(() => {
    const loadEmail = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user?.email) {
        setEmail(user.email);
      }
      setIsLoadingEmail(false);
    };
    loadEmail();
  }, []);

  const handleSendCode = async () => {
    if (!email) return;
    try {
      await sendOtp.mutateAsync({ method: 'email', destination: email });
      setStep('verify');
    } catch (_error) {
      // Error handled by mutation
    }
  };

  const handleVerify = async (code: string) => {
    try {
      // Combined verify + enroll in single call to avoid race conditions
      await enrollMfa.mutateAsync({ method: 'email', destination: email, code });
      onSuccess?.();
    } catch (_error) {
      // Error handled by mutation
    }
  };

  if (step === 'verify') {
    return (
      <OtpVerificationInput
        title={t('mfa.verifyEmail', 'Verify your email')}
        description={t('mfa.enterEmailCode', `Enter the 6-digit code sent to ${email}`)}
        onVerify={handleVerify}
        onBack={() => setStep('email')}
        onResend={handleSendCode}
        isPending={enrollMfa.isPending}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-5 w-5 text-primary" />
          {t('mfa.emailSetup', 'Set up Email Verification')}
        </CardTitle>
        <CardDescription>
          {t('mfa.emailSetupDescription', 'Receive a verification code via email each time you sign in')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email">{t('auth.email', 'Email')}</Label>
          <Input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoadingEmail}
            placeholder="you@example.com"
          />
          <p className="text-sm text-muted-foreground">
            {t('mfa.emailNote', 'We will send verification codes to this email')}
          </p>
        </div>

        <div className="flex gap-2 pt-4">
          {onCancel && (
            <Button variant="outline" onClick={onCancel}>
              {t('common.cancel', 'Cancel')}
            </Button>
          )}
          <Button 
            onClick={handleSendCode} 
            disabled={!email || sendOtp.isPending || isLoadingEmail}
          >
            {sendOtp.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Mail className="h-4 w-4 mr-2" />
            )}
            {t('mfa.sendVerificationCode', 'Send Verification Code')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
